/**
 * Chat Store - Global state management for AI chat feature
 * Uses Zustand for state management
 */

import { create } from 'zustand';
import type { ChatMessage, ChatState } from '@/types/chat';

interface ChatStore extends ChatState {
  // Actions
  addUserMessage: (content: string) => void;
  addAssistantMessage: (content: string, sources?: string[]) => void;
  updateLastMessage: (content: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearHistory: () => void;
  removeMessage: (id: string) => void;

  // Export functionality
  exportToText: () => string;
  exportToMarkdown: () => string;
  exportToJSON: () => string;
}

export const useChatStore = create<ChatStore>((set, get) => ({
  // Initial state
  messages: [],
  isLoading: false,
  error: null,

  // Add user message
  addUserMessage: (content) => {
    const message: ChatMessage = {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      content,
      timestamp: new Date(),
    };
    set((state) => ({
      messages: [...state.messages, message],
      error: null, // Clear any previous errors
    }));
  },

  // Add assistant message
  addAssistantMessage: (content, sources) => {
    const message: ChatMessage = {
      id: `assistant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: 'assistant',
      content,
      timestamp: new Date(),
      sources,
    };
    set((state) => ({
      messages: [...state.messages, message],
    }));
  },

  // Update the last message (useful for streaming)
  updateLastMessage: (content) => {
    set((state) => {
      const messages = [...state.messages];
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        messages[messages.length - 1] = {
          ...lastMessage,
          content,
        };
      }
      return { messages };
    });
  },

  // Set loading state
  setLoading: (loading) => set({ isLoading: loading }),

  // Set error state
  setError: (error) => set({ error }),

  // Clear all messages
  clearHistory: () => set({ messages: [], error: null }),

  // Remove specific message
  removeMessage: (id) => {
    set((state) => ({
      messages: state.messages.filter((msg) => msg.id !== id),
    }));
  },

  // Export to plain text
  exportToText: () => {
    const { messages } = get();
    let text = 'Chat History\n';
    text += '='.repeat(50) + '\n\n';

    messages.forEach((msg) => {
      const timestamp = msg.timestamp.toLocaleString();
      const role = msg.role === 'user' ? 'You' : 'AI Assistant';
      text += `[${timestamp}] ${role}:\n`;
      text += `${msg.content}\n`;

      if (msg.sources && msg.sources.length > 0) {
        text += '\nSources:\n';
        msg.sources.forEach((source) => {
          text += `- ${source}\n`;
        });
      }

      text += '\n' + '-'.repeat(50) + '\n\n';
    });

    return text;
  },

  // Export to markdown
  exportToMarkdown: () => {
    const { messages } = get();
    let markdown = '# Chat History\n\n';

    messages.forEach((msg) => {
      const timestamp = msg.timestamp.toLocaleString();
      const role = msg.role === 'user' ? '**You**' : '**AI Assistant**';

      markdown += `## ${role} (${timestamp})\n\n`;
      markdown += `${msg.content}\n\n`;

      if (msg.sources && msg.sources.length > 0) {
        markdown += '**Sources:**\n\n';
        msg.sources.forEach((source) => {
          markdown += `- ${source}\n`;
        });
        markdown += '\n';
      }

      markdown += '---\n\n';
    });

    return markdown;
  },

  // Export to JSON
  exportToJSON: () => {
    const { messages } = get();
    return JSON.stringify(
      {
        exportDate: new Date().toISOString(),
        messageCount: messages.length,
        messages: messages.map((msg) => ({
          ...msg,
          timestamp: msg.timestamp.toISOString(),
        })),
      },
      null,
      2
    );
  },
}));
